import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Badge } from "@/admin/ResourceTable";
import { Search, Loader2 } from "lucide-react";

const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const TONE = { active: "green", used: "blue", blocked: "red", expired: "amber", success: "green", invalid: "red" };
const Field = ({ label, value, testid }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div><div data-testid={testid} className="text-sm font-semibold text-gray-900 mt-0.5 break-words">{value || "—"}</div></div>
);

export default function CouponLookup() {
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const find = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true); setError(""); setData(null);
    try { const { data } = await api.get("/coupons/lookup", { params: { code: code.trim() } }); setData(data); }
    catch (err) { setError(apiErr(err)); } finally { setBusy(false); }
  };
  const c = data?.coupon;
  const expired = c?.expiry && c.expiry < new Date().toISOString() && c.status === "active";

  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Coupon Lookup</h1>
      <p className="text-sm text-gray-500 mt-1 mb-5">Search any coupon code to see its status, batch, SKU and scan history</p>
      <form onSubmit={find} className="flex gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
          <input data-testid="coupon-lookup-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Enter coupon code"
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg font-mono text-sm outline-none focus:border-[#8B0000]" />
        </div>
        <button data-testid="coupon-lookup-btn" disabled={busy} className="bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 rounded-lg flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Search</button>
      </form>
      {error && <div data-testid="coupon-lookup-error" className="mt-5 max-w-xl bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}
      {c && (
        <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_1fr]" data-testid="coupon-lookup-result">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-lg font-bold text-gray-900">{c.code}</span>
              <Badge tone={expired ? "amber" : TONE[c.status]}><span data-testid="coupon-lookup-status">{expired ? "expired" : c.status === "used" ? "redeemed" : c.status}</span></Badge>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-5">
              <Field label="Generated on" value={dt(c.created_at)} testid="coupon-lookup-created" />
              <Field label="Expiry" value={c.expiry ? dt(c.expiry) : "No expiry"} />
              <Field label="Batch" value={data.batch?.name} testid="coupon-lookup-batch" />
              <Field label="Batch for" value={data.batch?.target_role} />
              <Field label="SKU" value={data.product ? `${data.product.name} (${data.product.code})` : c.product_name} testid="coupon-lookup-sku" />
              <Field label="Token name" value={data.product?.token_name} />
              <Field label="Reward" value={`${c.reward_points} pts`} />
              <Field label="Redeemed on" value={c.used_at ? dt(c.used_at) : ""} />
              <Field label="Redeemed by" value={data.redeemed_by ? `${data.redeemed_by.name} · ${data.redeemed_by.mobile} (${data.redeemed_by.role})` : c.used_by_mobile} testid="coupon-lookup-redeemed-by" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 font-semibold text-gray-900">Scan history ({data.scans.length})</div>
            <table className="w-full text-sm text-left">
              <tbody>
                {data.scans.map((s, i) => (
                  <tr key={s.id || i} className="border-b border-gray-100">
                    <td className="py-2.5 px-4"><Badge tone={TONE[s.result]}>{s.result}</Badge></td>
                    <td className="py-2.5 px-4 font-mono text-xs">{s.mobile || "—"}</td>
                    <td className="py-2.5 px-4 text-xs">{s.dealer_name || "—"}</td>
                    <td className="py-2.5 px-4 text-xs text-gray-500">{dt(s.created_at)}</td>
                  </tr>
                ))}
                {data.scans.length === 0 && <tr><td className="py-8 text-center text-gray-400">No scans yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
